import _ from 'lodash'
import deepGet from "lodash/get"
import bookingPresetApi from '@/apis/liff/v2/booking-preset'
import collectionApi from '@/apis/liff/v2/collection'

const BRANCH_OCCUPY_CUSTOM_TYPE = 1

const isBranchOnlyOccupyMode = (occupyConfig) => {
  if (!occupyConfig || true !== deepGet(occupyConfig, 'enable', false)) {
    return false
  }
  return (
    true === deepGet(occupyConfig, 'is_check_branch', false) &&
    true !== deepGet(occupyConfig, 'is_check_staff', false) &&
    true !== deepGet(occupyConfig, 'is_check_service', false) &&
    true !== deepGet(occupyConfig, 'is_check_collection', false)
  )
}

const resolveEffectiveOccupyConfig = (presetOccupyConfig, branchOccupyConfig) => {
  if (!presetOccupyConfig) {
    return presetOccupyConfig
  }
  if (!isBranchOnlyOccupyMode(presetOccupyConfig)) {
    return presetOccupyConfig
  }
  if (!branchOccupyConfig || Number(branchOccupyConfig.type) !== BRANCH_OCCUPY_CUSTOM_TYPE) {
    return presetOccupyConfig
  }
  const branchText = deepGet(branchOccupyConfig, 'full_alert_text', null)
  const hasBranchAlertText = branchText != null && String(branchText).trim() !== ''
  return {
    ...presetOccupyConfig,
    slot_people: deepGet(branchOccupyConfig, 'slot_people', presetOccupyConfig.slot_people),
    full_alert_text: hasBranchAlertText
      ? branchText
      : deepGet(presetOccupyConfig, 'full_alert_text', ''),
  }
}

class OccupancyChecker {
  /**
   * @param {string|number} bookingPresetId
   * @param {Object} presetOccupyConfig booking_presets.config.occupy_config
   * @param {Object|null} [branchOccupyConfig] booking_presets 帶 branch_id 回傳的 branch_config.occupy_config
   */
  constructor(bookingPresetId, presetOccupyConfig, branchOccupyConfig = null) {
    this.presetOccupyConfig = presetOccupyConfig
    this.branchOccupyConfig = branchOccupyConfig
    this.bookingPresetId = bookingPresetId
    this.occupyConfig = resolveEffectiveOccupyConfig(presetOccupyConfig, branchOccupyConfig)
    this._initData()
  }

  setBranchOccupyConfig(branchOccupyConfig) {
    this.branchOccupyConfig = branchOccupyConfig
    this.occupyConfig = resolveEffectiveOccupyConfig(this.presetOccupyConfig, this.branchOccupyConfig)
    this._cleanAvailabilityResult()
    this._cleanOccupancyResult()
  }

  _initData() {
    this.dynamicFormInput = null
    this.startAt = null
    this.endAt = null
    this.collectionItem = null
    this.shop = null
    this.staff = null
    this.service = null
    this.hasPerformedOccupancyCheck = false
    this.isCheckingOccupancy = false
    this.hasPerformedAvailabilityCheck = false
    this.isCheckingAvailability = false
    this.occupancyResult = null
    this.availabilityResult = null
    this.occupancyCheckCallback = () => {}
    this.availabilityCheckCallback = () => {}
    this.latestReceivedCondition = null
    this.lastConditionType = null
    this.collectionUsedForMaximumCheck = null
    this.collectionIsBoundToBranch = false
    this.availabilityRequestId = 0
    this.occupancyRequestId = 0
  }

  async _checkAvailabilityWithAllConditions() {
    const requestId = ++this.availabilityRequestId
    this.isCheckingAvailability = true
    const parameters = {
      start_at: this.startAt,
      end_at: this.endAt,
    }

    if (deepGet(this.occupyConfig, 'is_check_branch', false)) {
      parameters.branch_id = this.shop
    }
    if (deepGet(this.occupyConfig, 'is_check_collection', false)) {
      parameters.collection_item_id = this.collectionItem
    }
    if (deepGet(this.occupyConfig, 'is_check_staff', false)) {
      parameters.staff_id = this.staff
    }
    if (deepGet(this.occupyConfig, 'is_check_service', false)) {
      parameters.service_ids = this.service
    }

    const availabilityResult = await bookingPresetApi.checkAvailability(this.bookingPresetId, parameters)
    if (requestId !== this.availabilityRequestId) {
      return
    }
    this.availabilityResult = availabilityResult
    this.hasPerformedAvailabilityCheck = true
    this.isCheckingAvailability = false
    await this.availabilityCheckCallback(this.availabilityResult)
  }

  async _checkOccupancyForTheLastCondition() {
    const parameters = {
      start_at: this.startAt,
      end_at: this.endAt,
    }

    if (deepGet(this.occupyConfig, 'is_check_branch', false) && this.shop) {
      parameters.branch_id = this.shop
    }
    if (deepGet(this.occupyConfig, 'is_check_collection', false) && this.collectionItem) {
      parameters.collection_item_id = this.collectionItem
    }
    if (deepGet(this.occupyConfig, 'is_check_staff', false) && this.staff) {
      parameters.staff_id = this.staff
    }
    if (deepGet(this.occupyConfig, 'is_check_service', false) && this.service) {
      parameters.service_ids = this.service
    }

    if (false === ['time', 'collection'].includes(this.occupyConfig.limit_type)) {
      console.warn(`[OccupancyChecker] Invalid limit type: ${this.occupyConfig.limit_type}`)
      return
    }

    if (this.lastConditionType !== 'time' && this.lastConditionType === this.latestReceivedCondition) {
      // already checked, skip
      return
    }

    this.isCheckingOccupancy = true
    const requestId = ++this.occupancyRequestId

    this._cleanOccupancyResult()

    const confirmedBookingsResponse = await bookingPresetApi.getOccupancy(this.bookingPresetId, parameters)
    if (requestId !== this.occupancyRequestId) {
      return
    }

    if (this.occupyConfig.limit_type === 'collection') {
      if (this.collectionIsBoundToBranch && this.shop) {
        this.collectionUsedForMaximumCheck = deepGet(await collectionApi.getOptionsForBranch(this.occupyConfig.collection_id, this.shop), 'data.data', null)
      } else {
        this.collectionUsedForMaximumCheck = deepGet(await collectionApi.getCollection(this.occupyConfig.collection_id), 'data.data', null)
      }
      if (requestId !== this.occupancyRequestId) {
        return
      }
    }

    if (this.lastConditionType === 'collection') { // the remaining condition is collection
      this.occupancyResult = {
        type: 'collection',
        disabled: await this._findOccupiedCollectionItems(confirmedBookingsResponse)
      }
    } else if (this.lastConditionType === 'shop') { // the remaining condition is shop
      this.occupancyResult = {
        type: 'shop',
        disabled: await this._findOccupiedShops(confirmedBookingsResponse)
      }
    } else if (this.lastConditionType === 'time') { // the remaining condition is time
      this.occupancyResult = {
        type: 'time',
        disabled: [],
        bookings: confirmedBookingsResponse
      }
    }
    this.hasPerformedOccupancyCheck = true
    this.isCheckingOccupancy = false
    await this.occupancyCheckCallback(this.occupancyResult)
  }

  async _findOccupiedCollectionItems(confirmedBookingsResponse) {
    let occupiedCollectionItems = (bookingCount) => {
      return bookingCount >= this.occupyConfig.slot_people
    }
    if (this.occupyConfig.limit_type === 'collection') {
      occupiedCollectionItems = (bookingCount, collectionItemId) => {
        return bookingCount >= this._getMaximumFromCollection(this.collectionUsedForMaximumCheck, collectionItemId)
      }
    }

    const groupedResult = _.groupBy(confirmedBookingsResponse, 'collection_item_id')
    return Object.keys(groupedResult)
      .filter((collectionItemId) => occupiedCollectionItems(groupedResult[collectionItemId].length, collectionItemId))
  }

  async _findOccupiedShops(confirmedBookingsResponse) {
    const groupedResult = _.groupBy(confirmedBookingsResponse, 'branch_id')
    return Object.keys(groupedResult)
      .filter(shopId => groupedResult[shopId].length >= this.occupancyMaximum())
  }

  _getMaximumFromCollection(collection, collectionItemId) {
    const collectionItem = deepGet(collection, 'items', []).find(item => item.id === collectionItemId)
    const maximumField = deepGet(collectionItem, 'data', []).find(field => field.field_id === this.occupyConfig.collection_field_id)
    return parseInt(deepGet(maximumField, 'value', 0))
  }

  _cleanOccupancyResult() {
    this.occupancyResult = null
    this.hasPerformedOccupancyCheck = false
  }

  _cleanAvailabilityResult() {
    this.availabilityResult = null
    this.hasPerformedAvailabilityCheck = false
  }

  _valueIsSet(value) {
    return false === [undefined, null, ''].includes(value)
  }

  _occupancyQueryConditions() {
    const conditions = {}

    if (deepGet(this.occupyConfig, 'is_check_collection', false)) {
      conditions.collection = {
        present: this._valueIsSet(this.collectionItem)
      }
    }

    if (deepGet(this.occupyConfig, 'is_check_branch', false)) {
      conditions.shop = {
        present: this._valueIsSet(this.shop)
      }
    }

    if (deepGet(this.occupyConfig, 'is_check_staff', false)) {
      conditions.staff = {
        present: this._valueIsSet(this.staff)
      }
    }

    if (deepGet(this.occupyConfig, 'is_check_service', false)) {
      conditions.service = {
        present: this._valueIsSet(this.service)
      }
    }

    return conditions
  }

  _fulfillsOccupancyCheckCondition() {
    const allQueryConditions = this._occupancyQueryConditions()
    return true === deepGet(this.occupyConfig, 'enable', false) &&
      (this.startAt !== null && this.endAt !== null) &&
      Object.keys(allQueryConditions)
        .filter(conditionType => conditionType !== this.lastConditionType)
        .every(conditionType => allQueryConditions[conditionType].present === true)
  }

  _fulfillsAvailabilityCheckCondition() {
    const allQueryConditions = this._occupancyQueryConditions()
    return true === deepGet(this.occupyConfig, 'enable', false) &&
      (this.startAt !== null && this.endAt !== null) &&
      Object.values(allQueryConditions).every(condition => condition.present === true)
  }

  setCollectionIsBoundToBranch(collectionIsBoundToBranch) {
    this.collectionIsBoundToBranch = collectionIsBoundToBranch
  }

  setLastConditionType(lastConditionType) {
    this.lastConditionType = lastConditionType
  }

  getOccupancyResult() {
    return this.occupancyResult
  }

  setOccupancyCheckCallback(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function')
    }
    this.occupancyCheckCallback = callback
  }

  getAvailabilityResult() {
    return this.availabilityResult
  }

  setAvailabilityCheckCallback(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function')
    }
    this.availabilityCheckCallback = callback
  }

  occupancyMaximum() {
    if (this.occupyConfig.limit_type === 'collection') {
      return this._getMaximumFromCollection(this.collectionUsedForMaximumCheck, this.collectionItem)
    }

    return Number(this.occupyConfig.slot_people) || 0
  }

  async receiveStartAt(startAt) {
    this.startAt = startAt
    this.latestReceivedCondition = 'time'

    this._cleanAvailabilityResult()
    await this._triggerCheckIfNecessary()
  }

  async receiveEndAt(endAt) {
    this.endAt = endAt
    this.latestReceivedCondition = 'time'

    this._cleanAvailabilityResult()
    await this._triggerCheckIfNecessary()
  }

  async receiveTimeRange(startAt, endAt) {
    this.startAt = startAt
    this.endAt = endAt
    this.latestReceivedCondition = 'time'

    this._cleanAvailabilityResult()
    await this._triggerCheckIfNecessary()
  }

  async receiveCollectionItem(collectionItem) {
    this.collectionItem = collectionItem
    this.latestReceivedCondition = 'collection'

    this._cleanAvailabilityResult()
    await this._triggerCheckIfNecessary()
  }

  async receiveShop(shop) {
    this.shop = shop
    this.latestReceivedCondition = 'shop'

    this._cleanAvailabilityResult()
    await this._triggerCheckIfNecessary()
  }

  async receiveStaff(staff) {
    this.staff = staff
    this.latestReceivedCondition = 'staff'

    this._cleanAvailabilityResult()
    await this._triggerCheckIfNecessary()
  }

  async receiveService(service) {
    this.service = service
    this.latestReceivedCondition = 'service'

    this._cleanAvailabilityResult()
    await this._triggerCheckIfNecessary()
  }

  async _triggerCheckIfNecessary() {
    await Promise.all([
      this._fulfillsOccupancyCheckCondition() ? this._checkOccupancyForTheLastCondition() : null,
      this._fulfillsAvailabilityCheckCondition() ? this._checkAvailabilityWithAllConditions() : null
    ])
  }
}

export default OccupancyChecker
